<template>
  <v-dialog
    v-model="visible"
    :max-width="type === 'success' ? 1100 : 800"
    persistent
  >
    <v-card v-if="visible" class="v-dialog--custom">
      <v-card-title class="headline pa-4">
        <span class="v-dialog--custom__title">{{ $t(`${namespace}.title.${type}`) }}</span>
        <v-btn
          class="v-dialog--custom__close mr-2"
          color="gray-6"
          flat
          fab
          @click.native="close(null)"
        >
          <v-icon dark>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="px-4 pt-0 pb-3">
        <div v-if="isSuccess">
          <v-data-table
            :headers="table.headers"
            :items="table.body"
            hide-actions
            color="accent"
            :no-results-text="$t(`${namespace}.noResultsText`)"
            :no-data-text="$t(`${namespace}.noDataText`)"
          >
            <template v-slot:items="props">
              <td>{{ props.item.companyId }}</td>
              <td v-if="getIncentiveType(props.item.type)">
                {{ getIncentiveType(props.item.type) }}
              </td>
              <td v-if="props.item.value">{{ props.item.value }}</td>
              <td v-if="props.item.startDate">
                {{ moment(props.item.startDate).format('DD/MM/YY') }}
              </td>
              <td v-if="props.item.endDate">
                {{ moment(props.item.endDate).format('DD/MM/YY') }}
              </td>
              <td v-if="typeof props.item.returnToOldIncentive === 'boolean'">
                {{ props.item.returnToOldIncentive ? $t('Common.yes') : $t('Common.no') }}
              </td>
              <td v-if="typeof props.item.updateIncentive === 'boolean'">
                {{ props.item.updateIncentive ? $t('Common.yes') : $t('Common.no') }}
              </td>
              <td v-if="isValidArray(props.item.errors)">
                <div
                  v-for="(error, index) in props.item.errors"
                  :key="`error-${index}`"
                  :title="error"
                  class="text--ellipsis py-1"
                >
                  {{ error }}<br>
                </div>
              </td>
            </template>
          </v-data-table>

          <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center mt-3">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </div>
        </div>

        <div v-if="isFailed" class="text-xs-center py-4">
          {{ $t('Common.genericErrorMsg') }}
          <br />
          <a
            :title="$t('Common.tryAgain')"
            class="accent--text"
            @click.prevent="loadData"
          >{{ $t('Common.tryAgain') }}.</a>
        </div>

        <div v-if="isLoading" class="text-xs-center py-4">
          <v-progress-circular indeterminate color="accent" />
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { isArray } from 'utils/functions'

export default {
  name: 'ApproveIncentiveDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'success'
    },
    data: {
      type: Object,
      default: () => {}
    }
  },
  computed: {
    isLoading () {
      return this.currentStatus === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },

    isFailed () {
      return this.currentStatus === STATUS_FAILED
    },

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    }
  },
  data () {
    return {
      namespace: 'Views.IncentivesPage.viewIncentivesDialog',
      dialog: false,
      currentStatus: null,
      pagination: {
        totalElements: 0,
        totalPages: 0,
        currentPage: 1,
        itemsPerPage: 6
      },
      table: {
        headers: [],
        body: []
      }
    }
  },
  methods: {
    isValidArray (val) {
      return isArray(val)
    },

    close (reload = null) {
      this.$emit('close', reload)
    },

    getIncentiveType (type) {
      const types = {
        PERCENT: this.$t(`${this.namespace}.percent`),
        CREDIT: this.$t(`${this.namespace}.credit`),
        SAAS_BY_RIDE: this.$t(`${this.namespace}.saasByRide`),
        GRANT: this.$t(`${this.namespace}.saasByRide`)
      }

      return types[type] || type
    },

    updatePagination (response, itemsPerPage) {
      this.pagination = {
        ...this.pagination,
        totalElements: response.totalElements,
        totalPages: response.totalPages
      }
    },

    async loadData () {
      this.currentStatus = STATUS_IS_LOADING

      try {
        const { currentPage, itemsPerPage } = this.pagination
        const incentiveId = this.data && this.data.id ? this.data.id : null
        const status = this.type ? this.type.toUpperCase() : null
        const { data: res } = await this.$apiService.incentives.getDetailById(
          incentiveId,
          { page: currentPage - 1 },
          { limit: itemsPerPage },
          { status }
        )

        if (res && this.isValidArray(res.content)) {
          this.renderTable(res.content)
          this.updatePagination(res, itemsPerPage)
        }

        this.currentStatus = STATUS_SUCCESS
      } catch (err) {
        console.log('ERROR:', err)
        this.currentStatus = STATUS_FAILED
      }
    },

    handlePage (page) {
      this.pagination = {
        ...this.pagination,
        currentPage: page
      }

      this.loadData()
    },

    renderTable (array) {
      if (this.type === 'success') {
        this.table.headers = [
          {
            text: this.$t(`${this.namespace}.companyId`),
            value: 'companyId',
            width: 100
          },
          {
            text: this.$t(`${this.namespace}.type`),
            value: 'type',
            width: 180
          },
          {
            text: this.$t(`${this.namespace}.value`),
            value: 'value'
          },
          {
            text: this.$t(`${this.namespace}.startDate`),
            value: 'startDate'
          },
          {
            text: this.$t(`${this.namespace}.endDate`),
            value: 'endDate'
          },
          {
            text: this.$t(`${this.namespace}.returnToOldIncentive`),
            value: 'returnToOldIncentive'
          },
          {
            text: this.$t(`${this.namespace}.updateIncentive`),
            value: 'updateIncentive'
          }
        ]

        this.table.body = array.map(item => {
          const { companyId, type, value, startDate, endDate, returnToOldIncentive, updateIncentive } = item
          return { companyId, type, value, startDate, endDate, returnToOldIncentive, updateIncentive }
        })
        return
      }

      if (this.type === 'error') {
        this.table.headers = [
          {
            text: this.$t(`${this.namespace}.companyId`),
            value: 'companyId',
            width: 100
          },
          {
            text: this.$t(`${this.namespace}.errors`),
            value: 'errors'
          }
        ]

        this.table.body = array.map(item => {
          const { companyId = '-', errors = [] } = item
          return { companyId, errors }
        })
      }
    }
  },
  watch: {
    visible (val) {
      val && this.loadData()
    }
  }
}
</script>
